import { Popover, PopoverInteractionKind, Position } from "@blueprintjs/core"
import * as PortalAction from "@src/Actions/PortalActions"
import {
  Content,
  Divider,
  FormContainer,
  FormElement,
  LinkWrapper,
  Panel,
  PanelContainer,
  PopoverContent,
  SubHeading,
} from "@src/Components/Common"
import { GetGenericErrorMassage } from "@src/Components/Common/Toast"
import { usePortalSize } from "@src/Components/Common/usePortalSize"
import FloatingLabelInput from "@src/Components/FloatingLabelInput"
import { Link } from "@src/Components/Link"
import PasswordInput from "@src/Components/PasswordInput"
import Waiting from "@src/Components/Waiting"
import C from "@src/Controller"
import { tState } from "@src/Model/Model"
import MessageContainer from "@src/Portal/MessageContainer"
import { Typography } from "@zeguro/component-library"
import { Button } from "@zeguro/component-library"
import * as _ from "lodash"
import * as React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useRouteMatch } from "react-router-dom"
const __filename = createFilename("Portal/Accounts/Login/index.tsx")

import { createFilename } from "@src/filepath"
import { colors } from "@src/theme"
import { ViewTypes } from "@src/types"

import { ButtonRow, Wait } from "./styles"

interface ILogin {
  history: any
}

interface IRoutesParams {
  subscriptionId?: string
}

const Login = (props: ILogin) => {
  const { clientHeight: height } = usePortalSize()
  const pContent = { height }

  const { accountsPageMode: mode, authenticated } = useSelector(
    (state: tState) => {
      return state.portal
    },
  )
  const { emailDomain } = useSelector((state: tState) => state.brand)

  React.useEffect(() => {
    if (authenticated) {
      props.history.push("/")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated])

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const verificationResult = urlParams.get("verification_result")
    const username = urlParams.get("username")

    if (verificationResult) {
      showVerificationToast(verificationResult, username)
      // Clean URL immediately
      window.history.replaceState({}, "", ViewTypes.signin)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showVerificationToast = (result: string, username: string | null) => {
    switch (result) {
      case "already_confirmed":
        C.toastNotification(
          "Your email address has already been verified.",
          "danger",
        )
        break

      case "expired":
        if (username) {
          const handleResend = () => {
            C.Cognito.resendConfirmationCode(username, (res: any) => {
              if (res.success) {
                C.toastNotification(
                  "Verification email has been sent. Please check your email.",
                  "success",
                )
              } else {
                C.toastNotification(
                  "Unable to resend verification email. Please try again later.",
                  "danger",
                )
              }
            })
          }

          C.toastNotification(
            <div>
              The link you clicked is no longer valid.
              <Button
                variant="tertiary"
                onClick={handleResend}
                margin="0 0 0 5px"
              >
                Request a new verification link
              </Button>
            </div>,
            "danger",
            0, // Persistent toast
          )
        } else {
          C.toastNotification("The verification link has expired.", "danger")
        }
        break

      case "invalid":
        C.toastNotification("The verification link is invalid.", "danger")
        break

      default:
        // All other errors - use standardized error message
        C.toastNotification(GetGenericErrorMassage(emailDomain), "danger")
    }
  }

  const dispatch = useDispatch()

  const [username, setUsername] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [newPassword, setNewPassword] = React.useState("")
  const [newPasswordValid, setNewPasswordValid] = React.useState(false)
  const [confirmPassword, setConfirmPassword] = React.useState("")
  const [confirmPasswordValid, setConfirmPasswordValid] = React.useState(false)
  const [verificationCode, setVerificationCode] = React.useState("")

  const setLoginPageView = (mode: string) => {
    dispatch(PortalAction.setAccountsPageMode(mode))
  }

  const onLogin = (username: string, password: string, newPassword: string) => {
    const trimmedUsername = username.trim()
    setUsername(trimmedUsername)
    C.heapIdentify(trimmedUsername)
    const currentLoginPageView = mode
    setLoginPageView("request")
    C.Cognito.authenticate(
      trimmedUsername,
      password,
      newPassword,
      (ret: any) => {
        if (ret.success) {
          if (ret.changePassword) {
            setLoginPageView("change")
          } else {
            // Clear verification email from localStorage on successful login
            localStorage.removeItem("verificationEmail")
            C.createSession(ret, () => {})
          }
        } else {
          C.toastNotification(ret.error, "danger")
          setLoginPageView(currentLoginPageView)
          C.heapResetIdentity()
        }
      },
    )
  }

  const requestVerificationCode = (username: string) => {
    const username_ = _.toLower(username.trim())
    C.Cognito.forgotPassword(
      username_,
      (msg: string) => {
        C.toastNotification(msg, "success")
        setLoginPageView("reset")
      },
      (msg: string) => {
        C.toastNotification(msg, "danger")
      },
    )
  }

  const resetPassword = (
    username: string,
    password: string,
    verificationCode: string,
  ) => {
    const username_ = _.toLower(username.trim())
    C.Cognito.resetPassword(username_, password, verificationCode, {
      onSuccess: () => {
        C.toastNotification("Your password has been reset", "success")
        setLoginPageView("login")
      },
      onFailure: (err: any) => {
        C.serverLog({
          level: "error",
          message: "Reset Password Failed",
          endpoint: "cognitoUser.confirmPassword",
          filename: __filename,
          stack: err,
        })
        C.toastNotification(err.message, "danger")
      },
    })
  }

  const alreadyHaveCode = () => {
    setLoginPageView("reset")
  }

  const match = useRouteMatch<IRoutesParams>([
    ViewTypes.signinWithSubscriptionId,
  ])
  const subscriptionId = match?.params?.subscriptionId
  const forgotPassword = () => {
    props.history.push(
      subscriptionId ? `/forgotPassword/${subscriptionId}` : "/forgotPassword",
    )
  }
  const redirectToSignup = () => {
    props.history.push(subscriptionId ? `/signup/${subscriptionId}` : "/signup")
  }

  const submitLogin = () => {
    const username_ = _.toLower(username.trim())
    const password_ = password.trim()
    const newPassword_ = newPassword.trim()

    if (username_.length > 0 && password_.length > 0) {
      onLogin(username_, password_, newPassword_)
      clearPasswords()
    } else {
      C.toastNotification("Please enter username and password", "danger")
    }
  }

  const startPasswordRecovery = () => {
    const username_ = _.toLower(username.trim())

    clearPasswords()
    if (username_.length > 0) {
      requestVerificationCode(username_)
      return
    } else {
      C.toastNotification("Please enter username", "danger")
    }
  }

  const submitPasswordChange = () => {
    const username_ = _.toLower(username.trim())
    const password_ = password.trim()
    const newPassword_ = newPassword.trim()
    const confirmPassword_ = confirmPassword.trim()

    if (
      password_.length > 0 &&
      newPassword_.length > 0 &&
      confirmPassword_.length > 0 &&
      _.isEqual(newPassword, confirmPassword)
    ) {
      onLogin(username_, password_, newPassword_)
    } else {
      if (password_.length === 0) {
        C.toastNotification(`Please enter the temporary password.`, "danger")
      } else if (newPassword_.length === 0 || confirmPassword_.length === 0) {
        C.toastNotification(
          `Please enter and confirm the new password.`,
          "danger",
        )
      } else if (!_.isEqual(newPassword_, confirmPassword_)) {
        C.toastNotification(
          `New password does not match the reentered password.`,
          "danger",
        )
      }
    }
  }

  const isMode = (modes: string[]) => {
    return _.includes(modes, mode)
  }

  const clearPasswords = () => {
    setPassword("")
    setNewPassword("")
  }

  return (
    <Content
      {...pContent}
      backgroundImage={
        !authenticated ? 'url("images/illo-office3.svg")' : "none"
      }
    >
      <MessageContainer />

      <PanelContainer height={height}>
        {!authenticated && (
          <Panel>
            {/* Regular Login Form */}

            <FormElement hidden={!isMode(["request"])}>
              <Wait>
                <Waiting text="Signing you in..." padding={5} height="100%" />
              </Wait>
            </FormElement>

            {/* Regular Login Form */}

            <FormContainer hidden={!isMode(["login"])} onSubmit={submitLogin}>
              <Typography
                variant="heading1"
                color={colors.darkBlue}
                align="center"
                margin="20px 0px"
              >
                Sign in to your account.
              </Typography>
              <FloatingLabelInput
                label="Work email address"
                type="email"
                value={username}
                onChange={({ target }) => setUsername(target?.value)}
                id="signInInput"
              />
              <FloatingLabelInput
                label="Password"
                type="password"
                value={password}
                onChange={({ target }) => setPassword(target?.value)}
                id="signInPassword"
              />
              <ButtonRow hidden={mode !== "login"}>
                <Button
                  width="100%"
                  variant="primary"
                  onClick={submitLogin}
                  id="sign-in-button"
                  disabled={!(username && password)}
                >
                  Sign In
                </Button>
              </ButtonRow>
            </FormContainer>

            {/* Password Reset Form */}

            <FormElement hidden={!isMode(["reset"])}>
              <Typography
                variant="heading1"
                color={colors.darkBlue}
                align="center"
                margin="20px 0px"
              >
                Reset your account password.
              </Typography>
              <FloatingLabelInput
                label="Work email address"
                type="email"
                value={username}
                onChange={({ target }) => setUsername(target?.value)}
                id="reset-password-email"
              />
              <FloatingLabelInput
                label="Verification code"
                type="text"
                value={verificationCode}
                onChange={({ target }) => setVerificationCode(target?.value)}
                id="reset-password-code"
              />
              <PasswordInput
                label="New password"
                value={newPassword}
                onChange={({ target }) => setNewPassword(target?.value)}
                id="reset-password-new"
                isValid={(valid) => setNewPasswordValid(valid)}
              />
              <ButtonRow>
                <Button
                  width="100%"
                  variant="primary"
                  onClick={() => {
                    resetPassword(username, newPassword, verificationCode)
                  }}
                  id="reset-password-button"
                  disabled={
                    !(
                      username &&
                      newPassword &&
                      verificationCode &&
                      newPasswordValid
                    )
                  }
                >
                  Reset Password
                </Button>
              </ButtonRow>
            </FormElement>

            {/* Password Change Form */}

            <FormElement hidden={!isMode(["change"])}>
              <Typography
                variant="heading1"
                color={colors.darkBlue}
                align="center"
                margin="20px 0px"
              >
                Change your password.
              </Typography>
              <Typography
                variant="heading2"
                color={colors.darkBlue}
                align="left"
              >
                Temporary Password
              </Typography>
              <SubHeading margin="0px" display="flex">
                Use the temporary password we emailed you. &nbsp;
                <Popover
                  content={
                    <PopoverContent>
                      If you can’t find the email, check other places like junk,
                      spam, social, or other folders.
                    </PopoverContent>
                  }
                  position={Position.TOP}
                  interactionKind={PopoverInteractionKind.HOVER}
                >
                  <Link>Can&apos;t find it?</Link>
                </Popover>
              </SubHeading>

              <FloatingLabelInput
                id="change-password-old"
                label="Temporary password"
                type="password"
                value={password}
                onChange={({ target }) => setPassword(target?.value)}
              />
              <Divider margin="20px 0px" />
              <Typography
                variant="heading2"
                color={colors.darkBlue}
                align="left"
              >
                New Password
              </Typography>
              <PasswordInput
                id="change-password-new"
                label="New password"
                value={newPassword}
                onChange={({ target }) => setNewPassword(target?.value)}
                isValid={(valid) => setNewPasswordValid(valid)}
                noIcon
              />
              <PasswordInput
                id="reenter-password-new"
                label="Reenter new password"
                value={confirmPassword}
                passwordToMatch={newPassword}
                onChange={({ target }) => setConfirmPassword(target?.value)}
                isValid={(valid) => setConfirmPasswordValid(valid)}
                noIcon
              />
              <ButtonRow>
                <Button
                  width="100%"
                  variant="primary"
                  id="change-password-button"
                  onClick={() => {
                    submitPasswordChange()
                  }}
                  disabled={
                    !(newPasswordValid && confirmPasswordValid && password)
                  }
                >
                  Change Password
                </Button>
              </ButtonRow>
            </FormElement>

            {/* Request Verification Key Form */}
            <FormElement hidden={!isMode(["forgot"])}>
              <Typography
                variant="heading1"
                color={colors.darkBlue}
                align="center"
                margin="20px 0px"
              >
                Verify your account.
              </Typography>
              <FloatingLabelInput
                id="verify-account-email"
                label="Work email address"
                type="email"
                value={username}
                onChange={({ target }) => setUsername(target?.value)}
              />
              <ButtonRow>
                <Button
                  width="100%"
                  variant="primary"
                  id="verify-account-request-code"
                  onClick={() => {
                    startPasswordRecovery()
                  }}
                  disabled={!username}
                >
                  Request Verification Code
                </Button>
              </ButtonRow>
              <LinkWrapper>
                <p>
                  <Link
                    onClick={() => {
                      clearPasswords()
                      alreadyHaveCode()
                    }}
                    id="already-have-verify-code-link"
                  >
                    Already have a verification code?
                  </Link>
                </p>
              </LinkWrapper>
            </FormElement>

            <LinkWrapper hidden={mode !== "login"}>
              <p>
                <Link
                  id="forgot-password-link"
                  onClick={() => {
                    clearPasswords()
                    forgotPassword()
                  }}
                >
                  I forgot my password
                </Link>
              </p>
            </LinkWrapper>
            <Divider hidden={mode !== "login"} />
            <LinkWrapper hidden={mode !== "login"}>
              <p>
                Don’t have an account? <br />
                <Link
                  onClick={() => {
                    clearPasswords()
                    redirectToSignup()
                  }}
                >
                  Sign up
                </Link>
              </p>
            </LinkWrapper>
          </Panel>
        )}
      </PanelContainer>
    </Content>
  )
}

export default Login
